import {
  AdjustmentType,
  OrderItem,
  OrderItemStatus,
} from '@oolio-group/domain';
import React, { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { FlatList, Text, View } from 'react-native';
import Mask from '../../component/Mask/Mask';
import { ScreenName } from '../../lib/enums';
import CartItem from './CartItem/CartItem';
import { useCart } from '../../hooks/cart/CartProvider';
import { useCartSetActiveScreen } from '../../hooks/useCartNavigationHelper/useCartSwitcherEffect';
import { usePreviousValue } from '@oolio-group/hooks';
import UpgradeModal from '../../component/Modals/UpgradeModal/UpgradeModal';
import { useModal } from '@oolio-group/rn-use-modal';
import { upsellComboStore } from '../../store/upsellCombo';
import { useTranslation } from '@oolio-group/localization';
import { getStyles } from './CartScreen.styles';
import { useTheme } from '../../hooks/useTheme';
import { Totals } from './CartTotals/CartTotals';
import MediaSection from './MediaSection/MediaSection';
import useBehaviorSubjectState from '../../hooks/rxjs/useSubjectState';
import { sessionSubject } from '../../state/sessionObservable';
import { resolveBrandColor, shouldShowAttribution } from '../../utils/branding';
import { sumPaymentSurcharge } from '../../utils/sumPaymentSurcharge';

const renderItem = ({ item }: { item: OrderItem; index: number }) => {
  return <CartItem item={item} />;
};

const CartScreen: React.FC = () => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);
  const { translate } = useTranslation();

  const flatListRef = useRef<FlatList<OrderItem>>(null);

  const { order } = useCart();
  const { showModal, closeModal } = useModal();

  useCartSetActiveScreen(ScreenName.CART);
  const { showUpsellComboModal } = useSyncExternalStore(
    upsellComboStore.subscribe,
    upsellComboStore.getSnapshot,
  );

  const totalTax = order.taxes?.reduce((sum, tax) => tax.amount + sum, 0) ?? 0;

  const discounts = order?.adjustments?.filter(
    adjustment => adjustment.adjustmentType == AdjustmentType.DISCOUNT,
  );

  const nonPaymentSurcharges = order.adjustments?.filter(
    adjustment =>
      adjustment.adjustmentType == AdjustmentType.SURCHARGE &&
      !adjustment.allowOnPaymentType,
  );

  const totalPaymentSurcharge = useMemo(
    () => sumPaymentSurcharge(order?.payments),
    [order?.payments],
  );

  const displayableOrderItems = useMemo(
    () =>
      order?.orderItems?.filter(
        item =>
          ![
            OrderItemStatus.CANCELLED,
            OrderItemStatus.VOID,
            OrderItemStatus.TRANSFERRED,
          ].includes(item.status),
      ),
    [order],
  );

  const scrollToEnd = () => {
    setTimeout(() => {
      flatListRef && flatListRef.current?.scrollToEnd();
    }, 100);
  };

  const previousLength = usePreviousValue(displayableOrderItems?.length);

  useEffect(() => {
    if (
      displayableOrderItems &&
      previousLength &&
      displayableOrderItems?.length > previousLength
    ) {
      scrollToEnd();
    }
  }, [displayableOrderItems, previousLength]);

  useEffect(() => {
    if (showUpsellComboModal) {
      showModal(<UpgradeModal />);
    } else {
      closeModal();
    }
  }, [showUpsellComboModal, showModal, closeModal]);

  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const display = session?.device?.deviceProfile?.customerDisplay;
  const config = display?.cartScreen;

  const customerName = useMemo(
    () => [order?.customer?.firstName].filter(Boolean).join(' '),
    [order?.customer?.firstName],
  );

  return (
    <View style={styles.container}>
      <View style={styles.lhs}>
        <Totals
          amountDue={order.amountDue ?? 0}
          subTotal={order.subTotal ?? 0}
          adjustments={order.adjustments ?? []}
          totalTax={totalTax}
          discounts={discounts}
          surcharges={nonPaymentSurcharges}
          paymentSurcharge={totalPaymentSurcharge}
        />
        <View style={styles.listWrapper}>
          <FlatList
            ref={flatListRef}
            data={displayableOrderItems}
            style={styles.list}
            renderItem={renderItem}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={
              order.orderNote?.trim() ? (
                <Text testID="order-note" style={styles.orderNote}>
                  {translate('cdsCartScreen.itemNote', {
                    note: order.orderNote.trim(),
                  })}
                </Text>
              ) : null
            }
          />
          <Mask height={48} containerStyle={styles.mask} />
        </View>
      </View>
      <MediaSection
        config={config}
        brandColor={resolveBrandColor(display?.brandColor)}
        customerName={customerName}
        storeName={session?.currentStore?.name}
        showAttribution={shouldShowAttribution(session?.currentOrganization)}
      />
    </View>
  );
};

export default CartScreen;
